import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { isMobile } from "react-device-detect";
import Joyride from "react-joyride";
import { useHistory, useLocation } from "react-router-dom";
import Button from "_shared/components/Button/Button";
import CustomModal from "_shared/components/Modal/Modal";
import { useGuidedTourQuery, useSetGuidedTourMutation } from "_shared/graphql";
import { analytics } from "_shared/utils/analytics";

const GuidedTourContext = createContext({
  openGuidedTour: () => {},
});

export function GuidedTourProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const history = useHistory();
  const [updateUserGuidedTour] = useSetGuidedTourMutation();
  const { data: userGuidedTour } = useGuidedTourQuery();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTourStarted, setIsTourStarted] = useState(false);

  useEffect(() => {
    if (userGuidedTour?.users?.[0]?.guided_tour) {
      setIsModalOpen(true);
    }
  }, [userGuidedTour?.users]);

  const onClose = () => {
    setIsTourStarted(false);
    setIsModalOpen(false);
    updateUserGuidedTour({
      variables: {
        on: false,
      },
    });
  };

  const onOpen = useCallback(() => {
    if (!location.pathname.startsWith("/dashboard")) {
      history.push("/dashboard/macro-etfs");
    }
    setIsModalOpen(true);
  }, []);

  return (
    <GuidedTourContext.Provider value={{ openGuidedTour: onOpen }}>
      {children}
      <CustomModal open={isModalOpen} onClose={onClose}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            width: 360,
            textAlign: "center",
          }}
        >
          <img
            src="/longbow_logo.svg"
            style={{ height: 60, marginBottom: 15 }}
            alt="Longbow logo"
          />
          <p
            style={{
              fontSize: 26,
              letterSpacing: 0.8,
            }}
          >
            Welcome to Longbow!
          </p>
          <p style={{ fontSize: 16, margin: "0 9px", padding: "0 5px" }}>
            Get familiar with your new dashboard by following our guided tour.
          </p>
        </div>
        <div className="footer-buttons">
          <Button
            data-testid="button-start"
            fullWidth
            onClick={async () => {
              onClose();
              setIsTourStarted(true);
              history.push("/dashboard/macro-etfs");
              analytics.track("Tour Started");
            }}
          >
            Start
          </Button>
        </div>
      </CustomModal>
      <Joyride
        run={isTourStarted}
        hideBackButton={true}
        continuous={true}
        disableScrolling={true}
        showProgress={true}
        disableOverlayClose={true}
        callback={({ action, index, type }) => {
          if (action === "close" || action === "reset") {
            return setIsTourStarted(false);
          }
          // hooks to change app state as we go back and forward through tour
          if (action === "next") {
            if (!document) return;
            if (index > 0 && index !== 8) {
              document.querySelector<HTMLElement>(".guidedTour2")?.click();
            }
            if (index === 3) {
              document.querySelector<HTMLElement>(".guidedTour3Click")?.click();
            } else if (index === 5) {
              document
                .querySelector<HTMLElement>(".guidedTour4")
                ?.dispatchEvent(
                  new MouseEvent("contextmenu", {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                  }),
                );
            } else if (index === 6) {
              document.querySelector<HTMLElement>(".guidedTour7")?.click();
            } else if (index === 7) {
              document
                .querySelector<HTMLElement>(".guidedTour8Hover")
                ?.dispatchEvent(
                  new MouseEvent("mouseover", {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                  }),
                );
            }
          }
          if (index === 8 && action === "update" && type === "step:before") {
            document
              .querySelector<HTMLElement>(".guidedTour8Hover")
              ?.dispatchEvent(
                new MouseEvent("mouseout", {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                }),
              );
            document.querySelector<HTMLElement>(".guidedTour2")?.click();
            analytics.track("Tour Completed");
          }
        }}
        styles={{
          options: {
            arrowColor: "#666",
            backgroundColor: "#000",
            beaconSize: 36,
            overlayColor: "rgba(0, 0, 0, 0)",
            primaryColor: "rgb(127, 218, 72)",
            spotlightShadow: "0 0 15px rgba(0, 0, 0, 0)",
            textColor: "#fff",
            zIndex: 2147483647,
            // height: "300px !important",
          },
          tooltip: {
            borderColor: "#666",
            borderWidth: 1,
            borderStyle: "solid",
          },
        }}
        steps={[
          // Dashboard Tabs
          {
            disableBeacon: true,
            target: ".guidedTour1",
            content: (
              <div style={{ textAlign: "left" }}>
                <p style={{ marginBottom: 20 }}>These are your panels.</p>
                <p>
                  You can select, rename, remove, and rearrange them as you see
                  fit.
                </p>
              </div>
            ),
            placement: "left-end",
            offset: -20,
          },
          // Add Tab Button
          {
            target: ".guidedTour2",
            content: "You can add a panel here",
          },
          // First ticker cell
          {
            target: ".guidedTour3",
            content:
              "Here are your tickers. You can click on the ticker to view detailed information.",
          },
          // Open Ticker Modal
          {
            target: ".guidedTour4",
            content: "You can switch between graphs for the ticker.",
          },
          // First column header
          {
            target: ".guidedTour5",
            content: "You can rearrange columns and rows as you see fit.",
          },
          // First ticker cell
          {
            target: ".guidedTour6",
            content: !isMobile
              ? 'Right click and select "Delete" to remove tickers.'
              : "Tap and hold to select 'Delete' button to remove tickers.",
            placement: "left-end",
          },
          // View buttons
          {
            target: ".guidedTour7",
            content: "Change the view to see your signals at various levels.",
          },
          // 'Account Navigation Link' on Desktop/Laptop
          // 'Hamburger Menu' on smaller devices.
          {
            target: !isMobile ? ".guidedTour8" : ".guidedTour8-mobile",
            content:
              "Edit your profile, manage your subscriptions, view your cards/invoices, and sign out here.",
            offset: !isMobile ? 140 : 0,
          },
          // Final step
          {
            target: ".guidedTour9",
            content: (
              <div>
                <i
                  style={{ fontSize: 40, marginBottom: 20 }}
                  className="justify-center check-icon far fa-check-circle"
                ></i>
                <p>You're all set! Start trading like a Pro with Longbow.</p>
              </div>
            ),
            placement: "center",
          },
        ]}
      />
    </GuidedTourContext.Provider>
  );
}

export const useGuidedTour = () => useContext(GuidedTourContext);
